import { useEffect, useMemo, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowsClockwise, Calendar, Crosshair, MapTrifold, Path, UserPlus,
  DotsSixVertical, Clock, X as XIcon, Plus, Minus,
} from "@phosphor-icons/react";
import ExpedienteQuickView from "@/components/expediente/ExpedienteQuickView";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const todayStr = () => new Date().toISOString().slice(0, 10);
// Ordinal en castellano: 1º, 2º, 3º…
const ordinal = (n) => `${n}º`;

/**
 * Página de Rutas presenciales con auto-agendado.
 *
 * Flujo:
 *  1) Filtros: "Salir desde el domicilio de:" (origen + agenda), "Asignado a:" (filtro),
 *     "Provincia".
 *  2) Selecciona expedientes con coordenadas.
 *  3) Acciones rápidas: "Asignármelos" (asigna al perito de origen) y "Optimizar".
 *  4) Optimizar → propuesta de ruta + agenda respetando horario laboral.
 *  5) "Agendar en mi calendario" → crea eventos en `calendar_events`.
 */
export default function Rutas() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [peritos, setPeritos] = useState([]);
  const [provinciasList, setProvinciasList] = useState([]);

  const [filterProvincia, setFilterProvincia] = useState("");
  const [origenPerito, setOrigenPerito] = useState("");
  const [filterAsignado, setFilterAsignado] = useState(""); // "" / "none" / perito_id

  // Selección de expedientes. Mantenemos también un array ordenado
  // (`selectedOrder`) para poder reordenar manualmente vía drag & drop sin
  // perder el orden cuando el usuario marca/desmarca.
  const [selected, setSelected] = useState(new Set());
  const [selectedOrder, setSelectedOrder] = useState([]);
  const [optimized, setOptimized] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [quickViewId, setQuickViewId] = useState(null);

  // Params de agendado
  const [fechaInicio, setFechaInicio] = useState(todayStr());
  const [duracion, setDuracion] = useState(20);
  const [buffer, setBuffer] = useState(5);
  // Overrides de duración por expediente (visitas complejas). Mapa
  // `{ expediente_id: minutos }`.
  const [durationOverrides, setDurationOverrides] = useState({});
  // Agenda del perito de origen para el día `fechaInicio` (eventos ya
  // existentes — visitas, juicios, etc. — para que el usuario vea qué
  // huecos tiene libres a la vez que va asignando rutas.).
  const [agendaEvents, setAgendaEvents] = useState([]);
  const [loadingAgenda, setLoadingAgenda] = useState(false);
  // Drag & drop reorder
  const [dragIdx, setDragIdx] = useState(null);

  const mapRef = useRef(null);
  const mapContainer = useRef(null);
  const markersRef = useRef([]);
  const routeLayerId = "ekspertium-route";

  // ---------- Carga de catálogos ----------
  useEffect(() => {
    api.get("/peritos").then((r) => setPeritos(r.data || [])).catch(() => {});
    api.get("/dashboard/produccion/provincias")
      .then((r) => setProvinciasList(r.data?.provincias || []))
      .catch(() => {});
  }, []);

  // ---------- Agenda del perito en `fechaInicio` ----------
  // Recargamos cuando cambia el perito o la fecha. Si hay ruta optimizada y
  // toca otro día, mostramos también los eventos del día principal de la
  // ruta para tener visibilidad completa.
  useEffect(() => {
    if (!origenPerito || !fechaInicio) {
      setAgendaEvents([]);
      return;
    }
    let cancelled = false;
    setLoadingAgenda(true);
    api.get("/calendar/events", {
      params: {
        perito_id: origenPerito,
        date_from: fechaInicio,
        date_to: fechaInicio,
      },
    })
      .then((r) => { if (!cancelled) setAgendaEvents(r.data || []); })
      .catch(() => { if (!cancelled) setAgendaEvents([]); })
      .finally(() => { if (!cancelled) setLoadingAgenda(false); });
    return () => { cancelled = true; };
  }, [origenPerito, fechaInicio]);

  // ---------- Carga de presenciales ----------
  const load = async () => {
    setLoading(true);
    setOptimized(null);
    try {
      const params = new URLSearchParams();
      if (filterProvincia) params.set("provincia", filterProvincia);
      if (filterAsignado) params.set("asignado_a", filterAsignado);
      const r = await api.get(`/routes/presenciales?${params.toString()}`);
      setItems(r.data || []);
      const ids = (r.data || []).filter((i) => i.lat).map((i) => i.id);
      setSelected(new Set(ids));
      setSelectedOrder(ids);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cargar la lista");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterProvincia, filterAsignado]);

  // Exponer un callback global para que los popups de Mapbox (HTML estático)
  // puedan abrir el panel lateral del expediente sin recargar la página.
  useEffect(() => {
    window.__rutasOpenExp = (id) => setQuickViewId(id);
    return () => { delete window.__rutasOpenExp; };
  }, []);

  // ---------- Init mapa ----------
  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;
    if (!mapboxgl.accessToken) {
      toast.error("Falta REACT_APP_MAPBOX_TOKEN");
      return;
    }
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center: [-3.7038, 40.4168],
      zoom: 5,
    });
    map.addControl(new mapboxgl.NavigationControl(), "top-right");
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // ---------- Marcadores ----------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!map.isStyleLoaded()) {
      const cb = () => updateMarkers();
      map.on("load", cb);
      return () => map.off("load", cb);
    }
    updateMarkers();
  }, [items, optimized]);

  const updateMarkers = () => {
    const map = mapRef.current;
    if (!map) return;
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    const route = optimized?.ordered || [];
    const pointsToShow = route.length ? route : items.filter((i) => i.lat && i.lng);

    if (pointsToShow.length === 0 && !optimized?.start) {
      try {
        if (map.getLayer(routeLayerId)) map.removeLayer(routeLayerId);
        if (map.getSource(routeLayerId)) map.removeSource(routeLayerId);
      } catch (err) {
        // Mapbox aún no ha terminado de cargar el estilo: silencioso, sólo en debug.
        if (process.env.NODE_ENV === "development") console.debug("[rutas] cleanup layer:", err);
      }
      return;
    }

    if (optimized?.start) {
      const el = document.createElement("div");
      el.style.cssText = "width:28px;height:28px;border-radius:50%;background:#16a34a;color:#fff;font-weight:700;font-size:11px;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,.3);";
      el.textContent = "★";
      const popup = new mapboxgl.Popup({ offset: 18 }).setHTML(`<div style="font-size:12px"><b>Salida</b><br/>${optimized.start.address}</div>`);
      markersRef.current.push(
        new mapboxgl.Marker(el).setLngLat([optimized.start.lng, optimized.start.lat]).setPopup(popup).addTo(map),
      );
    }

    pointsToShow.forEach((p, i) => {
      const el = document.createElement("div");
      el.style.cssText = "width:30px;height:30px;border-radius:50%;background:#E85D2C;color:#fff;font-weight:700;font-size:11px;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,.3);cursor:pointer;";
      el.textContent = ordinal(i + 1);
      const horaTxt = p.starts_at ? new Date(p.starts_at).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
      const popup = new mapboxgl.Popup({ offset: 18 }).setHTML(`
        <div style="font-size:12px;max-width:240px">
          <b>${ordinal(i + 1)} · ${p.aseguradora || ""}</b>
          ${p.asegurado ? `<br/><span>${p.asegurado}</span>` : ""}
          ${p.numero_siniestro ? `<br/><code style="font-family:monospace;font-size:11px">${p.numero_siniestro}</code>` : ""}
          ${p.address || p.direccion ? `<br/><span style="color:#666;font-size:11px">${p.address || p.direccion}</span>` : ""}
          ${p.telefono ? `<br/>📞 <a href="tel:${p.telefono}">${p.telefono}</a>` : ""}
          ${horaTxt ? `<br/><b style="color:#16a34a">🗓️ ${horaTxt}</b>` : ""}
          ${p.leg_km != null ? `<br/><b style="color:#E85D2C">${p.leg_km} km · ${p.travel_min || "?"} min</b>` : ""}
          <br/><button type="button" onclick="window.__rutasOpenExp && window.__rutasOpenExp('${p.id}')" style="margin-top:6px;background:#E85D2C;color:#fff;border:none;border-radius:4px;padding:4px 10px;font-weight:600;cursor:pointer;font-size:11px">Ver detalles →</button>
        </div>
      `);
      markersRef.current.push(
        new mapboxgl.Marker(el).setLngLat([p.lng, p.lat]).setPopup(popup).addTo(map),
      );
    });

    if (optimized && route.length && optimized.start) {
      const coordinates = [
        [optimized.start.lng, optimized.start.lat],
        ...route.map((r) => [r.lng, r.lat]),
      ];
      const geojson = { type: "Feature", geometry: { type: "LineString", coordinates } };
      if (map.getSource(routeLayerId)) {
        map.getSource(routeLayerId).setData(geojson);
      } else {
        map.addSource(routeLayerId, { type: "geojson", data: geojson });
        map.addLayer({
          id: routeLayerId, type: "line", source: routeLayerId,
          layout: { "line-join": "round", "line-cap": "round" },
          paint: { "line-color": "#E85D2C", "line-width": 3, "line-opacity": 0.7, "line-dasharray": [2, 1] },
        });
      }
    } else {
      try {
        if (map.getLayer(routeLayerId)) map.removeLayer(routeLayerId);
        if (map.getSource(routeLayerId)) map.removeSource(routeLayerId);
      } catch (err) {
        if (process.env.NODE_ENV === "development") console.debug("[rutas] cleanup layer:", err);
      }
    }

    const bounds = new mapboxgl.LngLatBounds();
    if (optimized?.start) bounds.extend([optimized.start.lng, optimized.start.lat]);
    pointsToShow.forEach((p) => bounds.extend([p.lng, p.lat]));
    if (!bounds.isEmpty()) map.fitBounds(bounds, { padding: 60, maxZoom: 13, duration: 600 });
  };

  // ---------- Acciones ----------
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setSelectedOrder((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  // Mueve el id `srcId` justo antes del id `dstId` dentro de la lista de
  // seleccionados. Si hay una ruta optimizada, llama al backend para
  // recalcular respetando el orden manual.
  const reorderSelected = (srcId, dstId) => {
    if (!srcId || !dstId || srcId === dstId) return;
    const arr = [...selectedOrder].filter((id) => selected.has(id));
    const from = arr.indexOf(srcId);
    const to = arr.indexOf(dstId);
    if (from < 0 || to < 0) return;
    const [moved] = arr.splice(from, 1);
    const insertAt = from < to ? to - 1 : to;
    arr.splice(insertAt, 0, moved);
    setSelectedOrder(arr);
    if (optimized?.ordered?.length > 0) {
      optimize(arr);
    } else {
      // Aviso suave para que el usuario sepa que el orden manual sólo se
      // aplica al pulsar Optimizar (de momento no se ha calculado nada).
      toast("Orden guardado · pulsa Optimizar para calcular horarios", { duration: 3500 });
    }
  };

  const assignToMe = async () => {
    if (!origenPerito) return toast.error("Elige primero el perito (campo 'Salir desde')");
    if (selected.size === 0) return toast.error("Selecciona al menos un expediente");
    setAssigning(true);
    try {
      const r = await api.post("/routes/assign", {
        perito_id: origenPerito,
        expediente_ids: Array.from(selected),
      });
      toast.success(`${r.data.updated} expediente(s) asignados a ${r.data.perito}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo asignar");
    } finally {
      setAssigning(false);
    }
  };

  const optimize = async (overrideOrderedIds = null) => {
    if (selected.size === 0) return toast.error("Selecciona al menos un expediente");
    if (!origenPerito) return toast.error("Selecciona el perito en 'Salir desde' (será el origen)");
    setOptimizing(true);
    try {
      // Si nos pasan un orden específico (drag & drop) lo respetamos; en
      // caso contrario usamos el orden manual del usuario (selectedOrder).
      const explicitOrder = overrideOrderedIds && overrideOrderedIds.length > 0;
      const expIds = explicitOrder
        ? overrideOrderedIds.filter((id) => selected.has(id))
        : selectedOrder.filter((id) => selected.has(id));
      const r = await api.post("/routes/optimize", {
        origen_perito_id: origenPerito,
        expediente_ids: expIds,
        fecha_inicio: fechaInicio,
        duracion_visita_min: duracion,
        buffer_min: buffer,
        usar_horario: true,
        preserve_order: !!explicitOrder,
        duracion_override: durationOverrides,
      });
      setOptimized(r.data);
      toast.success(
        `Ruta · ${r.data.ordered.length} paradas · ${r.data.total_km} km · ${Math.round(r.data.total_min)} min`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo optimizar la ruta");
    } finally {
      setOptimizing(false);
    }
  };

  // Reordena moviendo `srcIdx` justo delante de `dstIdx` y recalcula la
  // agenda respetando el nuevo orden manual.
  const reorderStops = async (srcIdx, dstIdx) => {
    if (!optimized?.ordered || srcIdx == null || dstIdx == null || srcIdx === dstIdx) return;
    const arr = [...optimized.ordered];
    const [moved] = arr.splice(srcIdx, 1);
    // Si quitamos un elemento antes del destino, el destino se desplaza −1
    const insertAt = srcIdx < dstIdx ? dstIdx - 1 : dstIdx;
    arr.splice(insertAt, 0, moved);
    await optimize(arr.map((s) => s.id));
  };

  // Ajusta la duración específica de una parada (visitas complejas) y
  // recalcula la ruta respetando el orden manual actual.
  const setStopDuration = async (expId, minutes) => {
    const next = { ...durationOverrides };
    const m = parseInt(minutes, 10);
    if (!m || isNaN(m) || m === duracion) {
      delete next[expId];
    } else {
      next[expId] = Math.max(5, Math.min(240, m));
    }
    setDurationOverrides(next);
    // Recalcula manteniendo el orden actual de la ruta optimizada
    if (optimized?.ordered?.length) {
      setOptimizing(true);
      try {
        const r = await api.post("/routes/optimize", {
          origen_perito_id: origenPerito,
          expediente_ids: optimized.ordered.map((s) => s.id),
          fecha_inicio: fechaInicio,
          duracion_visita_min: duracion,
          buffer_min: buffer,
          usar_horario: true,
          preserve_order: true,
          duracion_override: next,
        });
        setOptimized(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "No se pudo recalcular");
      } finally {
        setOptimizing(false);
      }
    }
  };

  const scheduleNow = async () => {
    if (!optimized?.ordered?.length) return;
    setScheduling(true);
    try {
      const r = await api.post("/routes/schedule", {
        perito_id: origenPerito,
        stops: optimized.ordered,
      });
      toast.success(`Agenda actualizada · ${r.data.created} creados, ${r.data.updated} actualizados`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo agendar");
    } finally {
      setScheduling(false);
    }
  };

  const withCoords = useMemo(() => items.filter((i) => i.lat && i.lng), [items]);
  const fmtHora = (iso) => iso ? new Date(iso).toLocaleString("es-ES", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl" data-testid="page-rutas">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-orange-600">Rutas presenciales</div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Path className="w-6 h-6 text-orange-600" weight="bold" />
            Planificador de visitas
          </h1>
          <p className="text-xs text-muted mt-1">
            Optimiza tus visitas con tiempo de conducción real (Mapbox) y agenda en tu calendario respetando tu horario laboral.
          </p>
        </div>
      </div>

      {/* Barra de filtros y parámetros */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-2 mb-4 bg-white border border-border rounded-lg p-3">
        <label className="flex flex-col gap-1 text-[11px] font-semibold text-muted">
          🚗 Salir desde el domicilio de
          <select
            value={origenPerito}
            onChange={(e) => setOrigenPerito(e.target.value)}
            className="text-xs border border-border rounded-md bg-white px-2 py-2 hover:border-foreground"
            data-testid="origen-perito"
          >
            <option value="">— Selecciona perito —</option>
            {peritos.map((p) => (
              <option key={p.id} value={p.id} disabled={!p.direccion_fiscal}>
                {p.name || p.full_name || p.email}{!p.direccion_fiscal ? " (sin domicilio)" : ""}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-[11px] font-semibold text-muted">
          👤 Asignado a
          <select
            value={filterAsignado}
            onChange={(e) => setFilterAsignado(e.target.value)}
            className="text-xs border border-border rounded-md bg-white px-2 py-2 hover:border-foreground"
            data-testid="filter-asignado"
          >
            <option value="">Todos</option>
            <option value="none">Sin asignar</option>
            {peritos.map((p) => (
              <option key={p.id} value={p.id}>{p.name || p.full_name || p.email}</option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-[11px] font-semibold text-muted">
          📍 Provincia
          <select
            value={filterProvincia}
            onChange={(e) => setFilterProvincia(e.target.value)}
            className="text-xs border border-border rounded-md bg-white px-2 py-2 hover:border-foreground"
            data-testid="filter-provincia"
          >
            <option value="">Todas</option>
            {provinciasList.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-[11px] font-semibold text-muted">
          🗓️ Fecha inicio
          <input
            type="date"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
            className="text-xs border border-border rounded-md bg-white px-2 py-2 hover:border-foreground"
            data-testid="fecha-inicio"
          />
        </label>

        <label className="flex flex-col gap-1 text-[11px] font-semibold text-muted">
          ⏱️ Duración visita (min)
          <input
            type="number"
            min={5}
            max={240}
            step={5}
            value={duracion}
            onChange={(e) => setDuracion(parseInt(e.target.value || 20, 10))}
            className="text-xs border border-border rounded-md bg-white px-2 py-2 hover:border-foreground"
            data-testid="duracion"
          />
        </label>

        <label className="flex flex-col gap-1 text-[11px] font-semibold text-muted">
          ⏸️ Buffer (min)
          <input
            type="number"
            min={0}
            max={60}
            step={1}
            value={buffer}
            onChange={(e) => setBuffer(parseInt(e.target.value || 0, 10))}
            className="text-xs border border-border rounded-md bg-white px-2 py-2 hover:border-foreground"
            data-testid="buffer"
          />
        </label>
      </div>

      {/* Acciones */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-xs border border-border bg-white hover:border-foreground rounded-md px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="reload-btn"
        >
          <ArrowsClockwise className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} weight="bold" />
          Recargar
        </button>
        <button
          type="button"
          onClick={assignToMe}
          disabled={assigning || selected.size === 0 || !origenPerito}
          className="text-xs border border-border bg-white hover:border-foreground rounded-md px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="assign-btn"
          title="Asigna los expedientes seleccionados al perito de origen"
        >
          <UserPlus className="w-3.5 h-3.5" weight="bold" />
          Asignármelos ({selected.size})
        </button>
        <button
          type="button"
          onClick={optimize}
          disabled={optimizing || selected.size === 0 || !origenPerito}
          className="btn-brand text-xs px-4 py-2 rounded-md font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="optimize-btn"
        >
          <Crosshair className="w-3.5 h-3.5" weight="bold" />
          {optimizing ? "Calculando..." : `Optimizar (${selected.size})`}
        </button>
        {optimized?.ordered?.length > 0 && (
          <button
            type="button"
            onClick={scheduleNow}
            disabled={scheduling}
            className="text-xs px-4 py-2 rounded-md font-semibold inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
            data-testid="schedule-btn"
          >
            <Calendar className="w-3.5 h-3.5" weight="bold" />
            {scheduling ? "Agendando..." : "Agendar en mi calendario"}
          </button>
        )}
      </div>

      {/* Resumen tras optimizar */}
      {optimized && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="optimization-result">
          <div className="text-sm">
            <span className="font-bold text-emerald-900">Ruta optimizada</span>
            <span className="text-emerald-700"> · {optimized.ordered.length} paradas · </span>
            <span className="font-bold text-emerald-900">{optimized.total_km} km · {Math.round(optimized.total_min)} min</span>
            <span className="text-emerald-700"> · {optimized.days_used?.length} día(s)</span>
            {optimized.skipped > 0 && <span className="text-amber-700"> · {optimized.skipped} sin coordenadas (omitidos)</span>}
            {!optimized.matrix_used && <span className="text-amber-700"> · estimación Haversine</span>}
          </div>
          <button type="button" onClick={() => setOptimized(null)} className="text-[11px] text-emerald-700 hover:underline">
            ✕ Cerrar ruta
          </button>
        </div>
      )}

      {/* Agenda del perito en `fechaInicio` (visible nada más elegir perito) */}
      {origenPerito && (
        <div className="mb-4 bg-white border border-border rounded-lg overflow-hidden" data-testid="agenda-perito-card">
          <header className="px-3 py-2 border-b border-border bg-slate-50 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-600" weight="duotone" />
            <h3 className="text-[12px] font-bold uppercase tracking-wider text-foreground">
              Agenda del perito · {new Date(`${fechaInicio}T00:00:00`).toLocaleDateString("es-ES", { weekday: "long", day: "2-digit", month: "short" })}
            </h3>
            {loadingAgenda && <span className="text-[10.5px] text-muted ml-auto">cargando…</span>}
          </header>
          <ul className="divide-y divide-border max-h-[260px] overflow-y-auto" data-testid="agenda-perito-list">
            {!loadingAgenda && agendaEvents.length === 0 && (
              <li className="px-3 py-4 text-center text-[11.5px] text-muted italic">
                El perito no tiene nada agendado este día. Día libre.
              </li>
            )}
            {agendaEvents
              .slice()
              .sort((a, b) => (a.starts_at || "").localeCompare(b.starts_at || ""))
              .map((ev) => {
                const tipoCls = {
                  visita: "bg-blue-100 text-blue-800 border-blue-200",
                  videoperitaje: "bg-emerald-100 text-emerald-800 border-emerald-200",
                  juicio: "bg-amber-100 text-amber-800 border-amber-200",
                  otro: "bg-zinc-100 text-zinc-700 border-zinc-200",
                }[ev.tipo] || "bg-zinc-100 text-zinc-700 border-zinc-200";
                return (
                  <li key={ev.id} className="px-3 py-2 flex items-center gap-2" data-testid={`agenda-evt-${ev.id}`}>
                    <span className="text-[11px] font-bold tabular-nums text-foreground w-12 flex-shrink-0">
                      {ev.starts_at && new Date(ev.starts_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                    <span className={`text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-px rounded border flex-shrink-0 ${tipoCls}`}>
                      {ev.tipo || "evento"}
                    </span>
                    <span className="text-[12px] text-foreground truncate flex-1">{ev.title || "(sin título)"}</span>
                  </li>
                );
              })}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-4">
        {/* Lista */}
        <div className="border border-border rounded-lg bg-white overflow-hidden">
          <div className="px-3 py-2 border-b border-border bg-slate-50 text-xs font-semibold flex items-center justify-between">
            <span>
              {loading ? "Cargando..." : `${items.length} expediente(s) · ${withCoords.length} con coordenadas`}
            </span>
            <button
              type="button"
              onClick={() => {
                const ids = withCoords.map((i) => i.id);
                setSelected(new Set(ids));
                setSelectedOrder(ids);
              }}
              className="text-[10px] text-orange-600 hover:underline"
              data-testid="select-all-btn"
            >
              Seleccionar todos
            </button>
          </div>
          <div className="max-h-[640px] overflow-y-auto divide-y divide-border" data-testid="exp-list">
            {!loading && items.length === 0 && (
              <div className="py-10 px-4 text-center text-xs text-muted">
                No hay expedientes presenciales con estos filtros.
              </div>
            )}
            {/* Renderizamos primero los seleccionados en el orden manual
                (o el orden de la ruta optimizada), y luego los no
                seleccionados. Los seleccionados son arrastrables. */}
            {(() => {
              const optimizedOrderIds = optimized?.ordered?.map((x) => x.id) || null;
              const effectiveOrder = optimizedOrderIds
                || selectedOrder.filter((id) => selected.has(id));
              const orderedSelected = effectiveOrder
                .map((id) => items.find((i) => i.id === id))
                .filter(Boolean);
              const notSelected = items.filter((i) => !selected.has(i.id));
              return [...orderedSelected, ...notSelected];
            })().map((it) => {
              const isSel = selected.has(it.id);
              const optimizedOrderIds = optimized?.ordered?.map((x) => x.id) || null;
              const effectiveOrder = optimizedOrderIds
                || selectedOrder.filter((id) => selected.has(id));
              const posInOrder = isSel ? effectiveOrder.indexOf(it.id) : -1;
              const ordIdx = posInOrder >= 0 ? posInOrder + 1 : null;
              const stopInfo = optimized && ordIdx ? optimized.ordered[ordIdx - 1] : null;
              const stopDuration = (stopInfo?.duracion_min)
                || durationOverrides[it.id]
                || duracion;
              const isComplex = stopDuration > duracion;
              const draggable = isSel;
              return (
                <div
                  key={it.id}
                  draggable={draggable}
                  onDragStart={(e) => {
                    if (!draggable) return;
                    setDragIdx(it.id);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(e) => { if (isSel) e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (isSel && dragIdx) reorderSelected(dragIdx, it.id);
                    setDragIdx(null);
                  }}
                  onDragEnd={() => setDragIdx(null)}
                  className={`flex items-start gap-2 p-3 hover:bg-slate-50 ${!it.lat ? "opacity-50" : ""} ${
                    dragIdx === it.id ? "bg-orange-50 opacity-60" : ""
                  } ${isSel ? "cursor-grab active:cursor-grabbing" : ""}`}
                  data-testid={`exp-${it.id}`}
                >
                  {isSel ? (
                    <DotsSixVertical className="w-4 h-4 text-zinc-400 mt-1 flex-shrink-0" weight="bold" title="Arrastra para reordenar" />
                  ) : (
                    <input
                      type="checkbox"
                      checked={false}
                      onChange={() => toggleSelect(it.id)}
                      disabled={!it.lat}
                      className="mt-1"
                    />
                  )}
                  {ordIdx && (
                    <button
                      type="button"
                      onClick={() => toggleSelect(it.id)}
                      title="Quitar de la ruta"
                      className="shrink-0 w-8 h-6 rounded-full bg-orange-600 hover:bg-orange-700 text-white text-[11px] font-bold flex items-center justify-center mt-0.5 px-1 cursor-pointer"
                    >
                      {ordinal(ordIdx)}
                    </button>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-xs font-semibold text-foreground truncate">{it.asegurado || "(sin nombre)"}</span>
                      <span className="text-[9px] uppercase bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded font-bold shrink-0">
                        {it.aseguradora}
                      </span>
                    </div>
                    <div className="text-[10px] text-muted truncate font-mono">{it.numero_siniestro || "—"}</div>
                    <div className="text-[10px] text-muted truncate mt-0.5">{it.direccion || "(sin dirección)"}</div>
                    {it.perito_asignado_name && (
                      <div className="text-[10px] text-indigo-700 mt-0.5">👤 {it.perito_asignado_name}</div>
                    )}
                    {stopInfo && (
                      <div className="text-[10px] text-emerald-700 mt-0.5 font-semibold">
                        🗓️ {fmtHora(stopInfo.starts_at)} · {stopInfo.leg_km} km · {stopInfo.travel_min} min
                      </div>
                    )}
                    {!it.lat && (
                      <div className="text-[10px] text-rose-600 mt-0.5">⚠️ Sin coordenadas — no se puede rutar</div>
                    )}
                    {isSel && (
                      <div className="flex items-center gap-1 mt-1.5" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          title="Quitar 5 minutos"
                          onClick={() => setStopDuration(it.id, stopDuration - 5)}
                          className="w-5 h-5 rounded border border-border text-muted hover:bg-zinc-100 inline-flex items-center justify-center"
                          data-testid={`dur-minus-${it.id}`}
                        >
                          <Minus className="w-2.5 h-2.5" weight="bold" />
                        </button>
                        <span
                          className={`text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${
                            isComplex ? "bg-amber-100 text-amber-800 border border-amber-200" : "bg-zinc-50 text-muted border border-border"
                          }`}
                          title={isComplex ? "Visita compleja — duración aumentada" : "Duración asignada"}
                        >
                          <Clock className="w-2.5 h-2.5" weight="bold" />
                          {stopDuration}m
                        </span>
                        <button
                          type="button"
                          title="Añadir 5 minutos (visita compleja)"
                          onClick={() => setStopDuration(it.id, stopDuration + 5)}
                          className="w-5 h-5 rounded border border-border text-muted hover:bg-zinc-100 inline-flex items-center justify-center"
                          data-testid={`dur-plus-${it.id}`}
                        >
                          <Plus className="w-2.5 h-2.5" weight="bold" />
                        </button>
                        {isComplex && (
                          <button
                            type="button"
                            title="Resetear a la duración por defecto"
                            onClick={() => setStopDuration(it.id, duracion)}
                            className="text-[10px] text-amber-700 hover:underline ml-1"
                          >
                            reset
                          </button>
                        )}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuickViewId(it.id); }}
                      className="text-[10px] text-orange-600 hover:underline mt-1 inline-block bg-transparent border-none p-0 cursor-pointer"
                      data-testid={`open-quick-${it.id}`}
                    >
                      Ver detalles →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mapa */}
        <div className="relative border border-border rounded-lg overflow-hidden bg-slate-100" style={{ minHeight: 640 }}>
          <div ref={mapContainer} style={{ position: "absolute", inset: 0 }} data-testid="map-container" />
          {withCoords.length === 0 && !loading && !optimized && (
            <div className="absolute inset-0 flex items-center justify-center text-xs text-muted bg-slate-50/80 backdrop-blur-sm pointer-events-none">
              <MapTrifold className="w-5 h-5 mr-2" /> Sin ubicaciones para mostrar
            </div>
          )}
        </div>
      </div>

      <ExpedienteQuickView expedienteId={quickViewId} onClose={() => setQuickViewId(null)} />
    </div>
  );
}
